import { z } from 'zod';
import { UserSchema } from './user.types.js';

/**
 * Shapes for the platform-admin console (`/admin/*`). These back the
 * **internal** generated client (`ui/src/client/generated-internal/`); the
 * public client never sees `/admin/*`. See
 * `docs/internal/architecture/api-contract.md`.
 */

/**
 * One holder of the assignable platform-admin role, as returned by
 * `GET /admin/app-admins`. The `user` carries the platform-admin view of the
 * account (includes `isSuperAdmin`/`status`); `roleSlug`/`roleId` identify the
 * granted app role.
 */
export const AppAdminSchema = z
  .object({
    user: UserSchema,
    roleSlug: z.string(),
    roleId: z.string(),
  })
  .meta({ id: 'AppAdmin' });
export type AppAdmin = z.infer<typeof AppAdminSchema>;

/** `{ admins }` envelope for `GET /admin/app-admins`. */
export const AppAdminListResponseSchema = z.object({
  admins: z.array(AppAdminSchema),
});
export type AppAdminListResponse = z.infer<typeof AppAdminListResponseSchema>;

/** `201` body for `POST /admin/app-admins` (grant). */
export const GrantAppAdminResponseSchema = z.object({
  ok: z.boolean(),
  userId: z.string(),
});
export type GrantAppAdminResponse = z.infer<typeof GrantAppAdminResponseSchema>;

/**
 * Generic `{ ok }` acknowledgement returned by admin-console mutations that
 * have nothing else to report (revoke app-admin, set user/org status, delete
 * a secret or a user provider link). Tagged so the client emits one reusable
 * `AdminOkResponse` type instead of per-operation duplicates.
 */
export const AdminOkResponseSchema = z.object({ ok: z.boolean() }).meta({ id: 'AdminOkResponse' });
export type AdminOkResponse = z.infer<typeof AdminOkResponseSchema>;

/**
 * Generic `{ success }` acknowledgement returned by the agent-model policy
 * mutations. Tagged so the client emits one reusable `AdminSuccessResponse`
 * type. (Separate from {@link AdminOkResponseSchema} only because those
 * handlers historically send `success` rather than `ok`.)
 */
export const AdminSuccessResponseSchema = z
  .object({ success: z.boolean() })
  .meta({ id: 'AdminSuccessResponse' });
export type AdminSuccessResponse = z.infer<typeof AdminSuccessResponseSchema>;
